import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { wrapDatafeedWithBinanceStream } from "../lib/binanceStreamer";
import { wrapDatafeedWithDhanStream } from "../lib/dhanStreamer";
import { wrapDatafeedWithServerTime } from "../lib/tvServerTime";
import { ChartCurrencyPicker } from "./ChartCurrencyPicker";
import { toast as _toast } from "sonner";

/**
 * TradingView Advanced Charts widget wrapper.
 *
 * REQUIREMENT: TradingView must have approved your Advanced Charts application
 * and you must have dropped their library files into
 *   /app/frontend/public/charting_library/
 * (see the README in that folder).
 *
 * Once approved:
 *   - Draggable native on-chart SL/TP/Entry order lines
 *   - Full drawing toolbar, saved layouts, replay mode, 100+ indicators
 *   - Same /api/tv-udf/* feed our Lightweight Charts already uses → chart price
 *     stays glued to the order-panel price with no drift.
 *
 * Until the library is present the component gracefully renders a "Coming soon"
 * fallback so the app never white-screens.
 */

const LIB_URL = "/charting_library/charting_library.js";

// Our own UDF-compatible datafeed. Serves our 16 hand-configured prop-sim
// symbols locally (matching the OrderPanel quotes exactly) AND transparently
// proxies through Yahoo Finance for every other global ticker — AAPL, TSLA,
// MSFT, NVDA, ETFs, indices, futures, forex, crypto — so the TradingView
// symbol search browses the full universe just like tradingview.com's demo.
const DATAFEED_URL_BASE = window.location.origin + "/api/tv-udf";

// The chart uses whatever raw symbol the caller passes. If it's one of our
// 16 tradable tickers, /api/tv-udf resolves it locally. Otherwise it falls
// through to Yahoo. Callers just pass the ticker string.
function toTvSymbol(sym) {
  if (!sym) return "XAUUSD";
  return String(sym);
}

/**
 * Overlay the user's currently-open positions and pending limit orders on
 * top of the chart via TradingView's `createPositionLine` / `createOrderLine`.
 * These are NATIVE ribbons that float on the right price scale — same
 * visualisation traders see in MT4/MT5.
 *
 * Advanced Charts licence renders these lines but they're NOT broker-editable
 * by drag (that requires the paid Trading Terminal licence). No-ops silently
 * if the /prop-sim/positions endpoint is unreachable or the chart isn't
 * ready yet — the chart itself keeps working.
 */
// Module-scoped in-flight guard for drawActivePositionLine. Any call
// site (`useEffect` on positions, `onSymbolChange`, initial mount, etc.)
// races through the SAME flag — a second call while a redraw is
// in-flight is dropped instead of stacking a duplicate horizontal_line
// on top of the previous one. Fixes the "4585.89 stacked 20× on the
// price scale" bug reported Feb 2026.
let _tj_drawing = false;

async function drawActivePositionLine(widget, symbol) {
  if (_tj_drawing) return;
  _tj_drawing = true;
  try {
    await _drawActivePositionLine(widget, symbol);
  } finally {
    _tj_drawing = false;
  }
}

async function _drawActivePositionLine(widget, symbol) {
  try {
    if (!widget || !symbol) return;
    // Fetch open positions AND pending limit orders for this symbol.
    let rows = [];
    try {
      const { data } = await api.get("/prop-sim/positions");
      rows = data?.positions || data || [];
    } catch (_) { rows = []; }
    const wantSym = String(symbol).toUpperCase();
    rows = rows.filter((p) => (p.symbol || "").toUpperCase() === wantSym && p.status === "open");

    const chart = widget.activeChart ? widget.activeChart() : null;
    if (!chart) return;

    // Clear previously drawn lines for this widget instance so switching
    // symbols doesn't accumulate stale ribbons. Robust: for each entry we
    // try both the OrderLine `.remove()` path AND the shape-wrapper
    // `remove()` path — createShape entities need chart.removeEntity but
    // that's already wrapped inside the pushed object below.
    if (widget._tj_lines) {
      widget._tj_lines.forEach((l) => {
        try { l?.remove?.(); } catch (_) {}
      });
    }
    widget._tj_lines = [];

    // Money formatter — MT5-style "$1,234.50 USD" running P&L badge.
    const fmt = (n) => {
      if (n === null || n === undefined || Number.isNaN(Number(n))) return "";
      const sign = n > 0 ? "+" : n < 0 ? "-" : "";
      const abs = Math.abs(Number(n));
      return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
    };

    // ---- MT5-style draggable SL/TP -----------------------------------
    // We import toast lazily so the chart component doesn't take a hard
    // dep on sonner if it's ever used outside the app shell.
    // Statically imported — this function runs on every position redraw and a
    // dynamic import() here re-ran the module resolution each time.
    const toastFn = _toast || null;

    // Persist a new SL/TP to the backend on drag-release. Rolls back the
    // line visually if the API rejects (e.g. stop on wrong side of price).
    const commitSltp = async (tradeId, field, newPrice, line, revertPrice) => {
      try {
        // Round to instrument decimals so we don't send silly precision.
        const decimals = window?.__tj_symbol_decimals ?? 2;
        const roundedPrice = Number(newPrice.toFixed(decimals));
        await api.post(`/prop-sim/trade/${tradeId}/sltp`, { [field]: roundedPrice });
        if (line) line.setPrice(roundedPrice);
        toastFn?.success?.(`${field === "stop_loss" ? "SL" : "TP"} → ${roundedPrice}`);
        // CRITICAL: fire the app-wide redraw event so the createShape bands
        // (which don't expose setPrice) get wiped and redrawn at the new
        // price. Without this, dragging SL/TP leaves ghost lines on the
        // chart at every previous position — exact bug user hit.
        try { window.dispatchEvent(new CustomEvent("tj:positions-updated")); } catch (_) {}
      } catch (e) {
        if (line && Number.isFinite(revertPrice)) line.setPrice(revertPrice);
        toastFn?.error?.(e?.response?.data?.detail || "Update failed");
      }
    };
    // Drag-off-screen gesture = remove SL/TP (send null). MT5 users know
    // this pattern reflexively — no extra button required.
    const cancelSltp = async (tradeId, field, line) => {
      try {
        await api.post(`/prop-sim/trade/${tradeId}/sltp`, { [field]: null });
        try { line?.remove(); } catch (_) {}
        toastFn?.success?.(`${field === "stop_loss" ? "SL" : "TP"} removed`);
        // Same reason as commitSltp above — the createShape band needs a
        // full redraw pass to disappear alongside the OrderLine.
        try { window.dispatchEvent(new CustomEvent("tj:positions-updated")); } catch (_) {}
      } catch (e) {
        toastFn?.error?.(e?.response?.data?.detail || "Remove failed");
      }
    };

    rows.forEach((p) => {
      const side = String(p.direction || p.side || "long").toLowerCase();
      const isLong = side === "long";
      // Fixed hardcoded colours matching TradingView's reference styling
      // (per user spec Feb 2026). No dynamic luminance calculation — the
      // dynamic path produced inconsistent results between the line and
      // the label pill. Colours are theme-aware ONLY for the ENTRY marker;
      // SL and TP use the same orange/teal in both themes because those
      // colours are already visible on any canvas.
      //
      // ENTRY  · dark canvas → white line + white pill + black text
      //        · light canvas → black line + black pill + white text
      // SL     · orange (#F97316) line + orange pill + white text (both themes)
      // TP     · teal   (#06B6D4) line + teal pill + white text (both themes)
      const _isDark = document.documentElement.classList.contains("dark");
      const entryColor = _isDark ? "#FFFFFF" : "#000000";
      const entryLabelText = _isDark ? "#000000" : "#FFFFFF";
      const slColor = "#F97316";
      const tpColor = "#06B6D4";
      const green = "#10b981";
      const red = "#ef4444";
      const upnl = Number(p.unrealized_pnl || 0);
      const upnlColor = upnl >= 0 ? green : red;
      // Bright entry-price highlight band. Colour is theme-aware (white on
      // dark canvas, near-black on light) so the marker never blends into
      // the background — see the "entry line invisible on dark" bug
      // reported Feb 2026. Label rendered as a filled pill (bg = line
      // colour, text = auto-contrast) so it stays readable over candles.
      try {
        const bandColor = entryColor;
        const shp = chart.createShape(
          { price: Number(p.entry_price) },
          {
            shape: "horizontal_line",
            lock: true,
            disableSelection: true,
            disableSave: true,
            disableUndo: true,
            overrides: {
              linecolor: bandColor,
              linewidth: 2,
              linestyle: 2, // dashed — reads as "reference band" not a trend line
              showLabel: true,
              text: `ENTRY ${Number(p.entry_price).toFixed(2)}`,
              textcolor: entryLabelText,
              backgroundColor: bandColor, // solid pill behind label
              borderColor: bandColor,
              horzLabelsAlign: "right",
              vertLabelsAlign: "top",
              fontsize: 11,
              bold: true,
            },
          }
        );
        if (shp) widget._tj_lines.push({ remove: () => { try { chart.removeEntity(shp); } catch (_) {} } });
      } catch (_) { /* older builds may not expose createShape/horizontal_line */ }
      // SL/TP full-width bands DROPPED — they were locked createShape
      // overlays that kept intercepting drag events even with `zOrder:
      // "bottom"` on some TV Charting Library builds. The OrderLine below
      // is already draggable AND already renders a full-width line via
      // `setLineLength(100)`; we just make it prominent enough to serve
      // as the visual band too.
      // SL line — DRAGGABLE + cancellable. Beefed-up label shows the price
      try {
        const line = chart.createPositionLine()
          .setText(`${side.toUpperCase()} · ${p.quantity} · ${fmt(upnl)}`)
          .setQuantity(String(p.quantity))
          .setPrice(Number(p.entry_price))
          .setLineColor(isLong ? green : red)
          .setBodyBackgroundColor(upnlColor)
          .setBodyBorderColor(upnlColor)
          .setBodyTextColor("#FFFFFF")
          .setQuantityBackgroundColor(isLong ? green : red)
          .setQuantityBorderColor(isLong ? green : red);
        widget._tj_lines.push(line);
      } catch (_) { /* older widget builds may not expose createPositionLine */ }
      // SL — full-width RED dashed line via createShape. Same rendering
      // path as the ENTRY band, which is guaranteed to work on every TV
      // Charting Library build. createOrderLine kept failing silently on
      // some builds when brittle setters (setLineLength / setLineStyle /
      // even .setEditable on old builds) threw and aborted the whole chain.
      // Trade-off: no on-chart drag — users update via the Modify dialog.
      if (p.stop_loss) {
        try {
          const slShp = chart.createShape(
            { price: Number(p.stop_loss) },
            {
              shape: "horizontal_line", lock: true, disableSave: true, disableUndo: true,
              overrides: {
                linecolor: "#F97316", linewidth: 2, linestyle: 0,
                showLabel: true, text: `SL ${Number(p.stop_loss).toFixed(2)}`,
                textcolor: "#FFFFFF",
                backgroundColor: "#F97316",
                borderColor: "#F97316",
                horzLabelsAlign: "right", vertLabelsAlign: "bottom",
                fontsize: 11, bold: true,
              },
            }
          );
          if (slShp) widget._tj_lines.push({ remove: () => { try { chart.removeEntity(slShp); } catch (_) {} } });
        } catch (_) {}
      }
      // TP — full-width GREEN dashed line via createShape. Same as SL above.
      if (p.take_profit) {
        try {
          const tpShp = chart.createShape(
            { price: Number(p.take_profit) },
            {
              shape: "horizontal_line", lock: true, disableSave: true, disableUndo: true,
              overrides: {
                linecolor: "#06B6D4", linewidth: 2, linestyle: 0,
                showLabel: true, text: `TP ${Number(p.take_profit).toFixed(2)}`,
                textcolor: "#FFFFFF",
                backgroundColor: "#06B6D4",
                borderColor: "#06B6D4",
                horzLabelsAlign: "right", vertLabelsAlign: "top",
                fontsize: 11, bold: true,
              },
            }
          );
          if (tpShp) widget._tj_lines.push({ remove: () => { try { chart.removeEntity(tpShp); } catch (_) {} } });
        } catch (_) {}
      }
    });
  } catch (e) {
    // Non-fatal — chart renders fine without the overlay.
    // eslint-disable-next-line no-console
    console.warn("[TvAdvancedChart] drawActivePositionLine failed:", e?.message || e);
  }
}

// LocalStorage-backed save/load adapter. Persists chart layouts, drawing
// tools, study templates, and drawing template groups per user. Survives
// symbol switches, page reloads, and browser restarts.
function makeLocalStorageAdapter(userId) {
  const PREFIX = `tj_tv_${userId}_`;
  const read  = (k) => { try { return JSON.parse(localStorage.getItem(PREFIX + k) || "{}"); } catch { return {}; } };
  const write = (k, v) => { try { localStorage.setItem(PREFIX + k, JSON.stringify(v)); } catch {} };
  return {
    // Layouts
    getAllCharts: async () => Object.values(read("charts")),
    removeChart: async (id) => { const c = read("charts"); delete c[id]; write("charts", c); },
    saveChart: async ({ name, content, symbol, resolution }) => {
      const id = Date.now();
      const c = read("charts");
      c[id] = { id, name: name || "Layout", content, symbol, resolution, timestamp: Math.floor(Date.now()/1000) };
      write("charts", c);
      return id;
    },
    getChartContent: async (id) => (read("charts")[id] || {}).content || "",
    // Study templates
    getAllStudyTemplates:  async () => Object.values(read("templates")),
    removeStudyTemplate:   async ({ name }) => { const t = read("templates"); delete t[name]; write("templates", t); },
    saveStudyTemplate:     async ({ name, content }) => { const t = read("templates"); t[name] = { name, content }; write("templates", t); },
    getStudyTemplateContent: async ({ name }) => (read("templates")[name] || {}).content || "",
    // Drawing templates (per-tool defaults)
    getDrawingTemplates:      async (toolName) => Object.keys(read(`draw_${toolName}`)),
    loadDrawingTemplate:      async (toolName, name) => read(`draw_${toolName}`)[name] || "",
    saveDrawingTemplate:      async (toolName, name, content) => { const d = read(`draw_${toolName}`); d[name] = content; write(`draw_${toolName}`, d); },
    removeDrawingTemplate:    async (toolName, name) => { const d = read(`draw_${toolName}`); delete d[name]; write(`draw_${toolName}`, d); },
    // Line-tools + groups (drawings persistence — the key one)
    loadLineToolsAndGroups:   async (chartLayoutId, chartId, requestType, docId) => {
      return read("linetools") || null;
    },
    saveLineToolsAndGroups:   async (chartLayoutId, chartId, requestContext, state) => {
      write("linetools", state);
    },
  };
}

// TradingView's Advanced Charts license is domain-locked. The agreement in
// your approval email allows only thetradlog.com — using the library on any
// other host (preview, staging, localhost) violates the T&Cs and can trigger
// a "heavy penalty" per the email from Vishal @ TradingView. We enforce that
// here so the widget refuses to mount on non-licensed hosts.
const LICENSED_HOSTS = ["thetradlog.com", "www.thetradlog.com"];
function isLicensedHost() {
  if (typeof window === "undefined") return false;
  return LICENSED_HOSTS.includes(window.location.hostname);
}

// Master toggle. Only "1" turns it on — anything else keeps LiveChart. Once
// the library is dropped in and prod is verified, set REACT_APP_TV_ADVANCED_CHART=1.
const FEATURE_ON = process.env.REACT_APP_TV_ADVANCED_CHART === "1";

const loadScript = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[src="${src}"]`)) return resolve();
  const s = document.createElement("script");
  s.src = src; s.async = true;
  s.onload = () => resolve();
  s.onerror = () => reject(new Error(`Failed to load ${src}`));
  document.head.appendChild(s);
});

// ---- Alternate chart sources (fallback iframes) ----------------------------
// When our primary datafeed (Yahoo → /api/tv-udf) freezes on a specific symbol,
// the user can flip to one of these TradingView public embed iframes which hit
// TradingView's own servers with a different upstream provider. If OANDA's feed
// is down we still have TVC (TradingView's own contributor). Two independent
// paths keeps charts working when either one hiccups. Only exposed for symbols
// listed here — everything else stays on the Tradelog datafeed only.
const ALT_SOURCES_BY_SYMBOL = {
  XAUUSD: [
    { id: "thinkmarkets", label: "TV · ThinkMarkets", tvSymbol: "THINKMARKETS:XAUUSD" },
    { id: "oanda", label: "TV · OANDA", tvSymbol: "OANDA:XAUUSD" },
    { id: "tvc",   label: "TV · TVC",   tvSymbol: "TVC:GOLD" },
  ],
  XAGUSD: [
    { id: "thinkmarkets", label: "TV · ThinkMarkets", tvSymbol: "THINKMARKETS:XAGUSD" },
    { id: "oanda", label: "TV · OANDA", tvSymbol: "OANDA:XAGUSD" },
    { id: "tvc",   label: "TV · TVC",   tvSymbol: "TVC:SILVER" },
  ],
  USOIL: [
    { id: "tvc",   label: "TV · TVC",   tvSymbol: "TVC:USOIL" },
    { id: "oanda", label: "TV · OANDA", tvSymbol: "OANDA:WTICOUSD" },
  ],
  // Indices: our upstream (Yahoo ^DJI/^NDX/^GSPC/^GDAXI) is rate-limited
  // erratically. Route through TradingView public embeds pinned to the
  // exact broker feeds the user requested — CFDs match retail terminals.
  US30: [
    { id: "capitalcom", label: "TV · CAPITAL.COM", tvSymbol: "CAPITALCOM:US30" },
  ],
  NAS100: [
    { id: "capitalcom", label: "TV · CAPITAL.COM", tvSymbol: "CAPITALCOM:US100" },
  ],
  SPX500: [
    { id: "oanda", label: "TV · OANDA", tvSymbol: "OANDA:SPX500USD" },
  ],
  GER40: [
    { id: "pepperstone", label: "TV · PEPPERSTONE", tvSymbol: "PEPPERSTONE:GER40" },
  ],
};
// Symbols where the primary Tradelog datafeed is unreliable — default the
// chart source to the first ALT entry (TV iframe) instead of "tradelog", so
// the chart is never blank on first open. Users can still flip back via the
// bottom-left switcher pills; per-symbol choice remains persisted.
// Symbols where the primary Tradelog datafeed is unreliable — default the
// chart source to the first ALT entry (TV iframe) instead of "tradelog", so
// the chart is never blank on first open. Users can still flip back via the
// bottom-left switcher pills; per-symbol choice remains persisted.
// Feb 2026: XAUUSD / XAGUSD dropped from this set — they now stream through
// Binance USDT-M Futures (XAUUSDT / XAGUSDT) via `binanceStreamer.js`, so
// the native Tradelog chart is fully live and tradeable. ThinkMarkets iframe
// stays available in the bottom-left switcher as backup.
const DEFAULT_ALT_SYMBOLS = new Set(["US30", "NAS100", "SPX500", "GER40"]);
function altSourcesFor(sym) {
  // Strip any `EXCHANGE:` prefix so `OANDA:XAUUSD` / `TVC:GOLD` still match
  // the base config below. Alias tickers (GOLD → XAUUSD etc.) are handled by
  // the backend canonicaliser; here we just need to look up alts by base.
  const raw = String(sym || "").toUpperCase();
  const tick = raw.includes(":") ? raw.split(":").pop() : raw;
  const aliasBase = { "GOLD": "XAUUSD", "SILVER": "XAGUSD", "WTICOUSD": "USOIL" };
  const base = aliasBase[tick] || tick;
  return ALT_SOURCES_BY_SYMBOL[base] || [];
}
function tvIframeUrl(tvSymbol, interval, theme = "dark") {
  const p = new URLSearchParams({
    symbol: tvSymbol, interval: String(interval || "15"),
    // ---- Drawing / tools ---------------------------------------------------
    // Show BOTH the top toolbar (interval, chart type, indicators) AND the
    // side toolbar (trendline, parallel channel, Fibonacci, long/short
    // position tool, etc.) so the user can draw on the fallback iframe just
    // like they can on the primary Tradelog widget.
    hidetoptoolbar: "0", hidesidetoolbar: "0",
    withdateranges: "1", details: "1", hotlist: "0", calendar: "0",
    hideideas: "1", saveimage: "1", allow_symbol_change: "0",
    theme, style: "1", locale: "en", timezone: "Etc/UTC",
  });
  return `https://s.tradingview.com/widgetembed/?${p.toString()}`;
}

export default function TvAdvancedChart({ symbol, interval = "5", height = 520, liveMode = false, onIntervalChange }) {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | missing | error | unlicensed | disabled
  // `null` = still loading. `{enabled, offset_days}` = loaded. Widget must
  // NOT be created until this is non-null, otherwise a race condition wraps
  // the datafeed with the Binance WS streamer before we know replay is on,
  // and current-live ticks leak into the last replay candle.
  const [replayInfo, setReplayInfo] = useState(liveMode ? { enabled: false, offset_days: 0 } : null);
  const [ccy, setCcy] = useState("USD");

  // ---- Chart source switcher (fallback for frozen upstream feeds) ---------
  // `source` = "tradelog" (default → our Advanced Charts + /api/tv-udf) or one
  // of the ALT_SOURCES_BY_SYMBOL entries. Persisted per-symbol in localStorage
  // so if the user found OANDA works for XAUUSD, we remember that choice.
  const symKey = String(symbol || "").toUpperCase();
  const alts = altSourcesFor(symbol);
  const [source, setSource] = useState(() => {
    try {
      const stored = localStorage.getItem(`tv_source_${symKey}`);
      if (stored) return stored;
    } catch {}
    // Symbols with no working Tradelog datafeed default to their first alt
    // (TV iframe) so the chart shows data on first open.
    if (DEFAULT_ALT_SYMBOLS.has(symKey)) {
      const a = ALT_SOURCES_BY_SYMBOL[symKey];
      if (a && a[0]) return a[0].id;
    }
    return "tradelog";
  });
  // Reset `source` when the user switches to a different symbol: pick the
  // stored preference for the new symbol, else fall back to the alt-default
  // rule above.
  useEffect(() => {
    let next = "tradelog";
    try {
      const stored = localStorage.getItem(`tv_source_${symKey}`);
      if (stored) next = stored;
      else if (DEFAULT_ALT_SYMBOLS.has(symKey)) {
        const a = ALT_SOURCES_BY_SYMBOL[symKey];
        if (a && a[0]) next = a[0].id;
      }
    } catch {}
    setSource(next);
  }, [symKey]);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  // Reset the loading spinner every time we mount a fresh iframe (either
  // because the user picked a different alt source or hit refresh).
  useEffect(() => { setIframeLoaded(false); }, [source, iframeKey, interval, symKey]);
  useEffect(() => {
    try { localStorage.setItem(`tv_source_${symKey}`, source); } catch {}
  }, [symKey, source]);
  // If the new symbol doesn't have an alt matching the current selection,
  // fall back to Tradelog so we don't show a broken pill.
  useEffect(() => {
    if (source !== "tradelog" && !alts.some(a => a.id === source)) setSource("tradelog");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symKey]);
  const activeAlt = alts.find(a => a.id === source);

  useEffect(() => {
    if (liveMode) return; // already initialized above
    api.get("/prop-sim/replay-info")
      .then(({ data }) => setReplayInfo(data || { enabled: false, offset_days: 0 }))
      .catch(() => setReplayInfo({ enabled: false, offset_days: 0 }));
  }, [liveMode]);

  useEffect(() => {
    let cancelled = false;
    // Skip widget creation entirely when user has flipped to an alt iframe
    // source; existing widget (if any) will be torn down by the return-cleanup
    // of the previous run.
    if (source !== "tradelog") return () => {};
    if (!FEATURE_ON) { setStatus("disabled"); return () => {}; }
    if (!isLicensedHost()) { setStatus("unlicensed"); return () => {}; }
    // Wait for replayInfo to load before creating the widget. Otherwise we
    // race and wrap the datafeed with the Binance WS streamer BEFORE we
    // know replay is on, and live ticks leak into replay candles.
    if (replayInfo === null) return () => {};
    (async () => {
      try {
        await loadScript(LIB_URL);
        if (cancelled) return;
        if (!window.TradingView || !window.TradingView.widget) {
          setStatus("missing"); return;
        }
        // Official UDF-compatible datafeed shipped by TradingView.
        // (Included in /datafeeds/udf/ of the private repo.)
        await loadScript("/datafeeds/udf/dist/bundle.js");
        if (cancelled || !window.Datafeeds) { setStatus("missing"); return; }

        // Base UDF datafeed polls /history every 1s (2nd ctor arg = ms).
        // liveMode routes to a mirror endpoint that ignores replay-offset,
        // so /charts always shows real-time market data.
        // Include ?ccy so backend converts OHLC into the user's currency.
        // The UDF datafeed builds URLs as `{base}/history?...` — appending
        // `?ccy=` to the base makes it show up as `?ccy=X&symbol=...` which
        // FastAPI handles fine.
        const _base = liveMode
          ? window.location.origin + "/api/tv-udf-live"
          : DATAFEED_URL_BASE;
        const DATAFEED_URL = ccy && ccy !== "USD" ? `${_base}?ccy=${ccy}` : _base;
        // 2nd arg = updateFrequency (ms). Was 10 000 (Feb 2026: too slow when
        // the WS relay is blocked by a proxy — chart could sit 10 s behind
        // real price). 3 000 gives sub-3-second refresh from /history poll
        // as a safety net without hammering upstream rate limits.
        const baseFeed = new window.Datafeeds.UDFCompatibleDatafeed(DATAFEED_URL, 3000);
        // Chain streamers: Dhan (MCX) on top, Binance (crypto/metals) below,
        // base UDF polling last. Each wrapper only intercepts its own
        // symbols and delegates everything else down the chain, so the
        // ordering doesn't matter — but keep Dhan outermost because MCX
        // is our newest and highest-priority live feed.
        const ccyGetter = () => ccy;
        // Wrap the base UDF feed with a bulletproof `getServerTime` before
        // any of the streamer proxies see it. The stock UDF library silently
        // swallows /time fetch errors — a single Cloudflare hiccup freezes
        // TV's internal clock forever and the countdown starts increasing
        // instead of counting down (00:46 → 01:46 → 05:09). See
        // /app/frontend/src/lib/tvServerTime.js for the full rationale.
        const timedFeed = wrapDatafeedWithServerTime(baseFeed, `${_base}/time`);
        const streamingFeed = replayInfo.enabled
          ? timedFeed
          : wrapDatafeedWithDhanStream(
              wrapDatafeedWithBinanceStream(timedFeed),
              { getCcy: ccyGetter }
            );

        widgetRef.current = new window.TradingView.widget({
          symbol: toTvSymbol(symbol),
          interval,
          container: containerRef.current,
          library_path: "/charting_library/",
          datafeed: streamingFeed,
          locale: "en",
          theme: "dark",
          autosize: true,
          fullscreen: false,
          // Most retail brokers (Exness, IC Markets, Pepperstone, FTMO, MFF)
          // run their MT4/MT5 servers on GMT+2/+3 (Cyprus / Athens time), so
          // their 15m/1h/D bars close 2-3 hours offset from UTC. Aligning our
          // chart bars to the same timezone means the candle boundaries line
          // up with what a trader sees in MetaTrader — same wick highs, same
          // body opens and closes, same support/resistance test points.
          timezone: "Etc/GMT-2",
          // Keep the built-in TradingView symbol search enabled so users can
          // pick ANY instrument that our /api/tv-udf/search endpoint returns.
          // We intentionally DO NOT disable "use_localstorage_for_settings" —
          // it's what lets TradingView auto-save drawings, indicators, and
          // theme choices across page reloads and symbol switches.
          disabled_features: [],
          enabled_features: [
            "study_templates",
            "hide_left_toolbar_by_default",
            // Explicitly show the header symbol search + resolution controls
            "header_symbol_search",
            "symbol_search_hot_key",
            "header_resolutions",
            "header_chart_type",
            "header_settings",
            "header_indicators",
            "header_compare",
            "header_undo_redo",
            "header_screenshot",
            "header_fullscreen_button",
            // "Unnamed ▾" chart-layout dropdown in the header.
            "header_saveload",
            // Bar replay mode (the little "play" icon in the top toolbar)
            "chart_zoom",
            "side_toolbar_in_fullscreen_mode",
            // Persist drawings/studies/layout to localStorage per-user.
            "use_localstorage_for_settings",
            "items_favoriting",
          ],
          auto_save_delay: 2,   // seconds between drawing edit → auto-persist
          save_load_adapter: makeLocalStorageAdapter((localStorage.getItem("tj_user_id") || "anon")),
          overrides: {
            "paneProperties.background": "#0a0a0a",
            "paneProperties.backgroundType": "solid",
          },
          custom_css_url: "",
          client_id: "tradelog.app",
          user_id: (localStorage.getItem("tj_user_id") || "anon"),
          load_last_chart: false,   // CRITICAL — do NOT auto-restore any
                                    // saved workspace across accounts. Was
                                    // causing account A's SL/TP shapes to
                                    // appear on account B's chart when both
                                    // used the same browser (TV localStorage
                                    // is shared per-origin). drawActivePositionLine
                                    // is the ONLY source of truth for our marks.
        });
        setStatus("ready");
        // Once the widget's chart is fully ready, draw a horizontal PositionLine
        // for the user's currently-active trade on this symbol. This is the
        // "on-chart entry marker" we can render with Advanced Charts (fully
        // draggable SL/TP order lines require the paid Trading Terminal
        // licence, not Advanced Charts — see the note in the file header).
        try {
          widgetRef.current.onChartReady(() => {
            // Belt-and-braces: even with load_last_chart:false, TV can
            // occasionally re-hydrate persisted shapes from its shared
            // localStorage bucket. Explicitly wipe every entity on the
            // chart before we draw our own, so account A's SL/TP can
            // NEVER surface on account B (same-browser account switch).
            try {
              const c = widgetRef.current.activeChart();
              const shapes = c.getAllShapes ? c.getAllShapes() : [];
              (shapes || []).forEach(s => { try { c.removeEntity(s.id); } catch (_) {} });
            } catch (_) {}
            drawActivePositionLine(widgetRef.current, symbol);
            // Force the chart to start on the parent's requested interval:
            // `load_last_chart: true` restores whatever TF was saved in
            // localStorage which can conflict with the parent's `interval`
            // prop. Snap to our own value on first ready so the outer
            // timeframe pill and the chart never disagree.
            try {
              const chart = widgetRef.current.activeChart();
              if (String(chart.resolution()) !== String(interval)) {
                chart.setResolution(String(interval));
              }
            } catch (_) { /* chart not ready yet */ }
            // Bubble interval changes up so the parent's own timeframe
            // selector stays in sync when the user uses TradingView's
            // built-in interval buttons. (Parent → child sync already
            // happens via the `interval` prop.)
            if (typeof onIntervalChange === "function") {
              try {
                widgetRef.current.chart().onIntervalChanged().subscribe(null, (iv) => {
                  try { onIntervalChange(String(iv)); } catch (_) {}
                });
              } catch (_) { /* silent — subscribe is optional */ }
            }
          });
        } catch (_) { /* silent - onChartReady is optional */ }
      } catch (e) {
        setStatus(e.message?.includes("Failed to load") ? "missing" : "error");
      }
    })();
    return () => {
      cancelled = true;
      try { widgetRef.current && widgetRef.current.remove(); } catch {}
      widgetRef.current = null;
    };
    // Re-create when replayInfo transitions from null → loaded so we know
    // whether to attach the Binance WS overlay. Symbol/interval changes are
    // still handled by the separate effect below via setSymbol().
    // Also depend on `source` so switching back to Tradelog re-creates a
    // fresh widget, and switching away tears the old one down cleanly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayInfo, ccy, source]);

  // Symbol changes ONLY. Kept separate from the interval effect below: a
  // combined effect made every timeframe click also re-set the symbol, which
  // reloads history at the resolution being left before loading the new one.
  useEffect(() => {
    const w = widgetRef.current;
    if (!w || status !== "ready") return;
    try {
      w.onChartReady(() => {
        try {
          const chart = w.activeChart();
          if (chart.symbol() === toTvSymbol(symbol)) return;
          // Chained, not raced — setSymbol/setResolution both return promises.
          Promise.resolve(chart.setSymbol(toTvSymbol(symbol)))
            .then(() => drawActivePositionLine(w, symbol))
            .catch(() => {});
        } catch (_) { /* chart not ready yet */ }
      });
    } catch (_) { /* silent */ }
  }, [symbol, status]);

  // Resolution changes ONLY. The equality guard also breaks a feedback loop:
  // onIntervalChange is wired to TradingView's own onIntervalChanged, so using
  // the library's built-in timeframe buttons sets parent state and re-enters
  // here — without the guard that reloads the chart a second time.
  useEffect(() => {
    const w = widgetRef.current;
    if (!w || status !== "ready") return;
    // Chart is already ready by the time status === "ready" — call
    // setResolution directly. Wrapping in onChartReady on subsequent
    // renders is a no-op on some TV builds where onChartReady only fires
    // once per widget lifetime, which was silently swallowing timeframe
    // changes and leaving the chart stuck on the previous resolution.
    try {
      const chart = w.activeChart();
      if (String(chart.resolution()) === String(interval)) return;
      chart.setResolution(String(interval));
    } catch (_) { /* silent */ }
  }, [interval, status]);

  // Listen for close / SL-TP mutations from anywhere in the app — the
  // OpenPositions close buttons, Close winners/losers, and the modify
  // dialog all dispatch `tj:positions-updated`. Redraw immediately so the
  // ENTRY / SL / TP bands wipe the moment a 100 % close (or SL/TP hit)
  // takes the trade off the books. Debounced 250 ms because a close-all
  // fires N events in the same tick.
  //
  // CONSOLIDATED with the 10s passive-drift redraw so `tj:positions-updated`
  // has ONE listener not two — two listeners were racing and stacking
  // duplicate ENTRY horizontal_line shapes on every update (the "4585.89
  // repeated 20 times on the price scale" bug reported Feb 2026).
  useEffect(() => {
    if (status !== "ready") return;
    let debounce = null;
    let drawing = false;
    const doRedraw = async () => {
      const w = widgetRef.current;
      if (!w || drawing) return;
      if (typeof document !== "undefined" && document.hidden) return;
      drawing = true;
      try {
        await drawActivePositionLine(w, symbol);
      } catch (_) { /* silent */ }
      finally { drawing = false; }
    };
    const debounced = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(doRedraw, 250);
    };
    // Instant redraw on mutation events + passive drift every 10s +
    // wake-up on tab visibility change. Every trigger routes through
    // the same `drawing` guard so a slow /prop-sim/positions response
    // can't be lapped by the next tick.
    window.addEventListener("tj:positions-updated", debounced);
    document.addEventListener("visibilitychange", doRedraw);
    const id = setInterval(doRedraw, 10000);
    return () => {
      window.removeEventListener("tj:positions-updated", debounced);
      document.removeEventListener("visibilitychange", doRedraw);
      clearInterval(id);
      if (debounce) clearTimeout(debounce);
    };
  }, [symbol, status]);

  return (
    <div className="relative w-full bg-neutral-950" style={{ height }} data-testid="tv-advanced-chart">
      {source === "tradelog" ? (
        <div ref={containerRef} className="absolute inset-0" />
      ) : activeAlt ? (
        <>
          <iframe
            key={`${activeAlt.id}-${iframeKey}-${interval}`}
            src={tvIframeUrl(activeAlt.tvSymbol, interval)}
            className="absolute inset-0 w-full h-full border-0"
            onLoad={() => setIframeLoaded(true)}
            allow="fullscreen"
            title={`TradingView chart · ${activeAlt.label}`}
            data-testid={`tv-alt-iframe-${activeAlt.id}`}
          />
          {/* Loading skeleton — shown while the TradingView iframe mounts
              (~2-3 s on slow connections). Prevents the "blank flash" the
              user flagged. */}
          {!iframeLoaded && (
            <div className="absolute inset-0 grid place-items-center bg-neutral-950"
                 data-testid="tv-alt-loading">
              <div className="flex flex-col items-center gap-3 opacity-70">
                <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                <p className="text-xs font-mono tracking-wider text-white/60">
                  LOADING · {activeAlt.label}
                </p>
              </div>
            </div>
          )}
          {/* Explicit "reference feed" disclaimer so users never confuse the
              alt-tab price with the price their trade fills at. */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[100] bg-amber-500/15 border border-amber-500/40 backdrop-blur px-3 py-1 rounded-full whitespace-nowrap text-[10px] font-semibold tracking-wide text-amber-300"
               data-testid="tv-alt-disclaimer">
            Reference feed · trades execute on Tradelog price
          </div>
        </>
      ) : null}

      {/* Chart-source switcher — only appears for symbols with configured
          alts (currently XAUUSD / XAGUSD / USOIL). Positioned at
          BOTTOM-LEFT so it never overlaps TradingView's own toolbar
          (symbol search, intervals, chart type — all in the top bar on
          production) or our currency picker (top-right). */}
      {alts.length > 0 && (
        <div
          className="absolute bottom-3 left-2 z-[100] flex items-center gap-0.5 bg-black/80 backdrop-blur rounded-full p-0.5 border border-white/15 shadow-lg"
          data-testid="chart-source-switcher">
          <button
            onClick={() => setSource("tradelog")}
            className={`px-2.5 py-1 text-[10px] font-bold tracking-wider rounded-full transition ${source === "tradelog" ? "bg-primary text-primary-foreground" : "text-white/60 hover:text-white"}`}
            title="Tradelog live datafeed"
            data-testid="chart-source-tradelog">
            TRADELOG
          </button>
          {alts.map(a => (
            <button key={a.id}
              onClick={() => setSource(a.id)}
              className={`px-2.5 py-1 text-[10px] font-bold tracking-wider rounded-full transition whitespace-nowrap ${source === a.id ? "bg-primary text-primary-foreground" : "text-white/60 hover:text-white"}`}
              title={`${a.label} · reference feed only — trades still execute on the Tradelog price`}
              data-testid={`chart-source-${a.id}`}>
              {a.label}
            </button>
          ))}
          <button
            onClick={() => {
              if (source === "tradelog") {
                // Force widget rebuild by toggling source through and back.
                const first = alts[0]?.id;
                if (first) { setSource(first); setTimeout(() => setSource("tradelog"), 30); }
              } else {
                // Bust iframe cache to force TradingView to reconnect.
                setIframeKey(k => k + 1);
              }
            }}
            className="w-7 h-7 grid place-items-center text-white/60 hover:text-white transition rounded-full hover:bg-white/10"
            title="Refresh chart"
            data-testid="chart-source-refresh">
            ↻
          </button>
        </div>
      )}

      {/* Currency picker — moved to BOTTOM-RIGHT (above the TradingView
          attribution) so it never overlaps the native fullscreen /
          settings / camera icons that live in TradingView's own top-
          right toolbar on production. */}
      <div className="absolute bottom-8 right-3 z-[100]">
        {source === "tradelog" && <ChartCurrencyPicker value={ccy} onChange={setCcy} />}
      </div>

      {source === "tradelog" && (status === "missing" || status === "unlicensed" || status === "disabled") && (
        <div className="absolute inset-0 grid place-items-center px-6 text-center">
          <div className="max-w-md space-y-3">
            <p className="text-[10px] uppercase tracking-widest text-primary">TradingView Advanced Charts</p>
            {status === "missing" && (
              <>
                <p className="text-white font-semibold text-lg">Library files not found</p>
                <p className="text-white/60 text-sm">
                  Drop the approved library into <code className="bg-white/10 px-1.5 py-0.5 rounded">/public/charting_library/</code>.
                  The datafeed at <code className="bg-white/10 px-1.5 py-0.5 rounded">/api/tv-udf</code> is already live.
                </p>
              </>
            )}
            {status === "unlicensed" && (
              <>
                <p className="text-white font-semibold text-lg">License restricted to thetradlog.com</p>
                <p className="text-white/60 text-sm">
                  The Advanced Charts library is only licensed to run on the production domain.
                  This host ({typeof window !== "undefined" ? window.location.hostname : ""}) falls back to Lightweight Charts to stay compliant with TradingView's agreement.
                </p>
              </>
            )}
            {status === "disabled" && (
              <>
                <p className="text-white font-semibold text-lg">Waiting on TradingView approval</p>
                <p className="text-white/60 text-sm">
                  Once the library files land, set <code className="bg-white/10 px-1.5 py-0.5 rounded">REACT_APP_TV_ADVANCED_CHART=1</code> and redeploy.
                </p>
              </>
            )}
            <a href="https://www.tradingview.com/advanced-charts/" target="_blank" rel="noreferrer"
               className="inline-block text-primary underline text-sm">TradingView Advanced Charts →</a>
          </div>
        </div>
      )}

      {/* Attribution — required by TradingView T&Cs. Sits at the very
          bottom-right corner, with the currency picker stacked directly
          above it and the chart-source switcher on the opposite corner. */}
      <a href="https://www.tradingview.com/" target="_blank" rel="noreferrer"
         className="absolute bottom-1 right-3 text-[9px] text-white/40 hover:text-white/80"
         data-testid="tv-attribution">
        Charts by TradingView
      </a>
    </div>
  );
}
